/**
 * `<DashboardView>` — the fourth board view (next to Table / Kanban /
 * Timeline): a widget grid assembled by the user, Looker-style but radically
 * simpler. Widgets aggregate the SAME in-memory rows the other views render,
 * so they respect the toolbar's search/filters and recompute instantly.
 *
 * Empty boards offer two paths: "Generate with AI" (the backend's LLM designs
 * a dashboard from the board's column profile; falls back to a deterministic
 * starter set when the LLM is unavailable) or manual "Add widget". The
 * assembled dashboard persists on the tracker's layout (`layout.dashboard`)
 * via the `onDashboardChange` callback — shared by the whole workspace.
 */
import { useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { LayoutDashboard, Plus, Sparkles } from 'lucide-react';

import type { DashboardWidget, TrackerDashboard } from '@/entities/tracker';
import { useSuggestDashboardMutation } from '@/entities/tracker';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';
import { EmptyState } from '@/shared/ui';

import {
  buildBoardProfile,
  buildDefaultDashboard,
  GRID_COLS,
  GRID_GAP,
  GRID_ROW_HEIGHT,
  type DashboardItem,
} from '../../lib/dashboard';
import type { CustomColumn } from '../../lib/useTrackersBoard';
import type { TrackerBoardType } from '../../lib/trackers-analytics';
import { DashboardWidgetBody } from './DashboardWidgets';
import { WidgetSettingsModal } from './WidgetSettingsModal';
import { WidgetShell } from './WidgetShell';
import { useCanEditTracker } from '../../lib/tracker-access';
import { Trans, useTranslation } from 'react-i18next';

interface DashboardViewProps {
  trackerId: string;
  boardType: TrackerBoardType;
  /** The board's (filtered) rows — same adapter the Kanban/Timeline views use. */
  items: DashboardItem[];
  customColumns: CustomColumn[];
  dashboard: TrackerDashboard | null;
  onDashboardChange: (next: TrackerDashboard) => void;
}

export function DashboardView(props: DashboardViewProps) {
  const { t } = useTranslation('trackers');

  const { trackerId, boardType, items, customColumns, dashboard, onDashboardChange } = props;
  const canEdit = useCanEditTracker();
  const widgets = useMemo(() => dashboard?.widgets ?? [], [dashboard]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  // The widget currently being edited (settings modal in edit mode).
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  // Confirm before regenerating — it REPLACES the whole current dashboard.
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  // Selected widget (click) — shows its ring + resize handle, monday-style.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const suggestMutation = useSuggestDashboardMutation({ trackerId });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const emit = (nextWidgets: DashboardWidget[]) => onDashboardChange({ widgets: nextWidgets });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = widgets.findIndex((w) => w.id === String(active.id));
    const to = widgets.findIndex((w) => w.id === String(over.id));
    if (from < 0 || to < 0) return;
    emit(arrayMove(widgets, from, to));
  };

  // Add (new id) or edit (existing id) — the settings modal builds the widget.
  const submitWidget = (widget: DashboardWidget) => {
    const exists = widgets.some((w) => w.id === widget.id);
    if (exists) {
      emit(widgets.map((w) => (w.id === widget.id ? widget : w)));
    } else {
      emit([...widgets, widget]);
      track('tracker_dashboard_widget_added', {
        tracker_id: trackerId,
        board_type: boardType,
        widget_type: widget.type,
      });
    }
  };
  const removeWidget = (id: string) => {
    const widgetType = widgets.find((w) => w.id === id)?.type;
    emit(widgets.filter((w) => w.id !== id));
    if (widgetType) {
      track('tracker_dashboard_widget_removed', {
        tracker_id: trackerId,
        board_type: boardType,
        widget_type: widgetType,
      });
    }
  };
  const renameWidget = (id: string, title: string) =>
    emit(widgets.map((w) => (w.id === id ? { ...w, title } : w)));
  const resizeWidget = (id: string, w: number, h: number) =>
    emit(widgets.map((widget) => (widget.id === id ? { ...widget, w, h } : widget)));

  /**
   * AI generation: send the compact board profile, take the LLM's widgets;
   * an empty answer (no key / outage) falls back to the deterministic
   * starter dashboard so the button ALWAYS produces something.
   */
  const generate = async () => {
    try {
      const profile = buildBoardProfile(items, customColumns);
      const response = await suggestMutation.mutateAsync(profile);
      if (response.widgets.length > 0) {
        emit(response.widgets);
        notificationService.showSuccess(t('trackersBoard.dashboardView.dashboardGenerated'));
        track('tracker_dashboard_generated', {
          tracker_id: trackerId,
          board_type: boardType,
          method: 'ai',
          widget_count: response.widgets.length,
        });
      } else {
        const fallback = buildDefaultDashboard(customColumns);
        emit(fallback);
        notificationService.showSuccess(t('trackersBoard.dashboardView.addedAStarterDashboard'));
        track('tracker_dashboard_generated', {
          tracker_id: trackerId,
          board_type: boardType,
          method: 'fallback',
          widget_count: fallback.length,
        });
      }
    } catch {
      const fallback = buildDefaultDashboard(customColumns);
      emit(fallback);
      notificationService.showSuccess(t('trackersBoard.dashboardView.addedAStarterDashboard'));
      track('tracker_dashboard_generated', {
        tracker_id: trackerId,
        board_type: boardType,
        method: 'fallback',
        widget_count: fallback.length,
      });
    }
  };

  const generating = suggestMutation.isPending;
  const columnById = useMemo(() => new Map(customColumns.map((c) => [c.id, c])), [customColumns]);

  if (widgets.length === 0 && !canEdit) {
    return (
      <EmptyState
        variant="outline-dashed"
        className="h-full min-h-80 rounded-2xl"
        icon={<LayoutDashboard aria-hidden />}
        title={t('trackersBoard.viewOnly.noDashboardYet')}
        descriptionMaxWidth="24rem"
        description={t('trackersBoard.viewOnly.noDashboardDescription')}
      />
    );
  }

  if (widgets.length === 0) {
    return (
      <>
        <EmptyState
          variant="outline-dashed"
          className="h-full min-h-80 rounded-2xl"
          icon={<LayoutDashboard aria-hidden />}
          title={t('trackersBoard.dashboardView.assembleYourDashboard')}
          descriptionMaxWidth="24rem"
          description={t('trackersBoard.dashboardView.visualizeThisTrackerWithKpis')}
        >
          <div className="flex items-center gap-2">
            <Button type="button" disabled={generating} onClick={() => void generate()}>
              <Sparkles aria-hidden />
              {generating
                ? t('trackersBoard.dashboardView.designing')
                : t('trackersBoard.dashboardView.generateWithAi')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setAddModalOpen(true)}>
              <Plus aria-hidden />
              {t('trackersBoard.dashboardView.addWidget')}
            </Button>
          </div>
        </EmptyState>
        <WidgetSettingsModal
          open={addModalOpen}
          onOpenChange={setAddModalOpen}
          columns={customColumns}
          items={items}
          onSubmit={submitWidget}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-3">
      {canEdit ? (
        <div className="flex items-center justify-end gap-2">
          {/* Provider is local: this app mounts no global one (see DateTimeCell). */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              {/* The tooltip is unreachable while generating, because Button's
               base sets disabled:pointer-events-none and Radix opens on
               pointerenter or focus. Accepted: the copy describes the action
               rather than the disabled reason, and the label already reads
               "Designing…". Wrapping the trigger in a focusable span to get it
               back would put a non-interactive element in the tab order, which
               is the worse trade. */}
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="mini"
                  disabled={generating}
                  onClick={() => setRegenConfirmOpen(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Sparkles className="size-3.5" aria-hidden />
                  {generating
                    ? t('trackersBoard.dashboardView.designing')
                    : t('trackersBoard.dashboardView.regenerateWithAi')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('trackersBoard.dashboardView.replaceTheCurrentWidgetsWith')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            type="button"
            variant="outline"
            size="mini"
            onClick={() => setAddModalOpen(true)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-3.5" aria-hidden />
            {t('trackersBoard.dashboardView.addWidget')}
          </Button>
        </div>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
          {/* The "invisible grid": 12 equal columns, fixed-height rows, dense
              packing so resized widgets tile without gaps. Clicking the empty
              backdrop clears the selection. */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click clears selection; a convenience, not a control */}
          <div
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedId(null);
            }}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
              gridAutoRows: `${GRID_ROW_HEIGHT}px`,
              gridAutoFlow: 'row dense',
              gap: `${GRID_GAP}px`,
            }}
          >
            {widgets.map((widget) => (
              <WidgetShell
                key={widget.id}
                widget={widget}
                selected={selectedId === widget.id}
                onSelect={() => setSelectedId(widget.id)}
                onEdit={() => setEditingWidget(widget)}
                onRename={(title) => renameWidget(widget.id, title)}
                onResize={(w, h) => resizeWidget(widget.id, w, h)}
                onDelete={() => removeWidget(widget.id)}
              >
                <DashboardWidgetBody
                  widget={widget}
                  items={items}
                  column={
                    widget.config?.columnId
                      ? (columnById.get(widget.config.columnId) ?? null)
                      : null
                  }
                />
              </WidgetShell>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <WidgetSettingsModal
        open={addModalOpen || editingWidget !== null}
        initial={editingWidget}
        onOpenChange={(next) => {
          if (!next) {
            setAddModalOpen(false);
            setEditingWidget(null);
          }
        }}
        columns={customColumns}
        items={items}
        onSubmit={submitWidget}
      />

      <AlertDialog open={regenConfirmOpen} onOpenChange={setRegenConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trackersBoard.dashboardView.regenerateThisDashboard')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="trackersBoard.dashboardView.replaceAllCurrentWidgets"
                ns="trackers"
                count={widgets.length}
                values={{ count: widgets.length }}
                components={{ strong: <strong /> }}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('trackersBoard.dashboardView.keepCurrent')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRegenConfirmOpen(false);
                void generate();
              }}
            >
              {t('trackersBoard.dashboardView.regenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
